import { Forms } from './forms'
import { FieldView } from '../lib/form-logic'

interface ValidationErrorAsText {
  text: string
}

interface ValidationErrorAsMarkup {
  markup: string
}

export type ValidationError = ValidationErrorAsText | ValidationErrorAsMarkup
export type MaybeValidationError = ValidationError | null

class CustomFieldView {
  protected $el: JQuery<HTMLElement>
  private $row: JQuery<HTMLElement>
  private $field: JQuery<HTMLElement>
  private previousError: MaybeValidationError = null

  public constructor($el: JQuery<HTMLElement>) {
    if ($el.length !== 1) throw new Error('Expected 1 element')

    this.$el = $el
    this.$row = $el.parents('.c-form-row')
    this.$field = $el.parents('.c-form-row_field')
  }

  public isChecked() {
    return this.$el.is(':checked')
  }

  public isFocused() {
    return this.$el.is(':focus')
  }

  public getDebugName() {
    return this.$el.attr('name') || this.$el.attr('id') || '??'
  }

  public setValidationError(error: MaybeValidationError) {
    if (errorsEqual(this.previousError, error)) return

    this.clearError()
    if (error) {
      this.markAsError()
      if (isValidationErrorAsMarkup(error)) {
        this.$row.append(error.markup)
      } else {
        Forms.showError(this.$row, error.text)
      }
    }

    this.previousError = error
  }

  public setValidating(validating: boolean) {
    this.$field.toggleClass('u-is-loading-input', validating)
  }

  public setValid(valid: boolean) {
    this.$el.toggleClass('is-valid', valid)
    if (valid) {
      this.clearError()
    }
  }

  public isActive() {
    return this.isFocused()
  }

  public activate() {
    this.$el.focus()
  }

  public addEventListener(event: string, callback: () => void) {
    this.$el.on(event, callback)
  }

  private clearError() {
    this.$row.removeClass('error').find('.c-form-row_error').remove()
  }

  private markAsError() {
    this.setValid(false)
    this.$row.addClass('error')
  }
}

export class StringFieldView
  extends CustomFieldView
  implements FieldView<string, ValidationError>
{
  public getValue(): string {
    const val = this.$el.val()

    if (typeof val !== 'string') {
      throw new Error('Expected string as value')
    }

    return val
  }
}

export class BooleanFieldView
  extends CustomFieldView
  implements FieldView<boolean, ValidationError>
{
  public getValue(): boolean {
    return this.isChecked()
  }
}

function isValidationErrorAsMarkup(
  err: ValidationError
): err is ValidationErrorAsMarkup {
  return (err as ValidationErrorAsMarkup).markup !== undefined
}

function errorsEqual(e1: MaybeValidationError, e2: MaybeValidationError) {
  if (e1 && e2) {
    if (isValidationErrorAsMarkup(e1)) {
      if (isValidationErrorAsMarkup(e2)) {
        return e1.markup === e2.markup
      }
    } else if (!isValidationErrorAsMarkup(e2)) {
      return e1.text === e2.text
    }
  }
  return e1 === e2
}
